/**
 * GraveForge Centralized API Client
 * 
 * All database operations go through the API server.
 * ZERO direct Supabase calls from the frontend.
 * 
 * Uses the same auth pattern as serverApi.js (x-api-key + x-wallet-address headers).
 */

import { getConfig } from './publicConfig';

const API_KEY = import.meta.env.VITE_SERVER_API_KEY;

const API_TIMEOUT = 30000;

function getBaseUrl() {
  if (getConfig('features.export_proxy', false)) return ''; // Nginx proxy — relative paths
  return import.meta.env.VITE_SERVER_URL || 'http://localhost:3002';
}

// ---------------------------------------------------------------------------
// Core helpers
// ---------------------------------------------------------------------------

function getHeaders(walletAddress = null, isFormData = false) {
  const headers = {};

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  if (API_KEY) {
    headers['x-api-key'] = API_KEY;
  }

  if (walletAddress) {
    headers['x-wallet-address'] = walletAddress; // Kept for backward compat during migration
  }

  // Attach JWT for server-side wallet extraction (preferred over x-wallet-address)
  const jwt = localStorage.getItem('boneforge_jwt');
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }

  return headers;
}

async function fetchWithTimeout(url, options = {}, timeout = API_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - server may be busy');
    }
    const msg = error?.message || '';
    if (error.name === 'TypeError' && (msg.includes('Failed to fetch') || msg.includes('NetworkError'))) {
      throw new Error('Cannot reach the GraveForge API server. Make sure the server is running.');
    }
    throw error;
  }
}

async function request(method, path, { body, walletAddress, timeout, isFormData = false } = {}) {
  const url = `${getBaseUrl()}/graveforge${path}`;
  const options = {
    method,
    headers: getHeaders(walletAddress, isFormData),
  };

  if (body !== undefined) {
    options.body = isFormData ? body : JSON.stringify(body);
  }

  const response = await fetchWithTimeout(url, options, timeout || API_TIMEOUT);

  if (!response.ok) {
    let errorMsg = `API error ${response.status}`;
    try {
      const errData = await response.json();
      errorMsg = errData.error || errData.message || errorMsg;
    } catch {
      // ignore parse errors
    }
    throw new Error(errorMsg);
  }

  return response.json();
}

const get    = (path, opts) => request('GET', path, opts);
const post   = (path, opts) => request('POST', path, opts);
const patch  = (path, opts) => request('PATCH', path, opts);
const put    = (path, opts) => request('PUT', path, opts);
const del    = (path, opts) => request('DELETE', path, opts);

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export const projects = {
  list(walletAddress) {
    return get(`/projects?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  getById(projectId, walletAddress) {
    return get(`/projects/${projectId}`, { walletAddress });
  },
  create(projectData, walletAddress) {
    return post('/projects', { body: projectData, walletAddress });
  },
  update(projectId, updates, walletAddress) {
    return patch(`/projects/${projectId}`, { body: updates, walletAddress });
  },
  delete(projectId, walletAddress) {
    return del(`/projects/${projectId}`, { walletAddress });
  },
  getWithPermissions(projectId, walletAddress) {
    return get(`/projects/${projectId}/with-permissions?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  archive(projectId, reason, walletAddress) {
    return post(`/projects/${projectId}/archive`, { body: { reason }, walletAddress });
  },
  unarchive(projectId, walletAddress) {
    return post(`/projects/${projectId}/unarchive`, { walletAddress });
  },
  getArchiveStatus(projectId, walletAddress) {
    return get(`/projects/${projectId}/archive-status`, { walletAddress });
  },
  getInactive(walletAddress) {
    return get('/projects/inactive', { walletAddress });
  },
  getAccessible(walletAddress) {
    return get(`/projects/accessible?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  getArchiveSummary(walletAddress) {
    return get('/projects/archive-summary', { walletAddress });
  },
  updateArchiveSettings(projectId, inactiveDays, autoArchive, walletAddress) {
    return patch(`/projects/${projectId}/archive-settings`, {
      body: { inactive_days_before_archive: inactiveDays, auto_archive: autoArchive },
      walletAddress
    });
  },
};

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------
export const layers = {
  list(projectId, walletAddress) {
    return get(`/layers?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  create(layerData, walletAddress) {
    return post('/layers', { body: layerData, walletAddress });
  },
  update(layerId, updates, walletAddress) {
    return patch(`/layers/${layerId}`, { body: updates, walletAddress });
  },
  delete(layerId, walletAddress) {
    return del(`/layers/${layerId}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------
export const images = {
  create(imageData, walletAddress) {
    return post('/images', { body: imageData, walletAddress });
  },
  update(imageId, updates, walletAddress) {
    return patch(`/images/${imageId}`, { body: updates, walletAddress });
  },
  delete(imageId, walletAddress) {
    return del(`/images/${imageId}`, { walletAddress });
  },
  /**
   * Upload image files (original + thumbnail + preview) + create DB record.
   * @param {FormData} formData - Must include files and metadata fields
   */
  upload(formData, walletAddress, timeout = 60000) {
    return post('/images/upload', { body: formData, walletAddress, isFormData: true, timeout });
  },
  replace(formData, walletAddress, timeout = 60000) {
    return post('/images/replace', { body: formData, walletAddress, isFormData: true, timeout });
  },
  bulkUpload(formData, walletAddress, timeout = 120000) {
    return post('/images/bulk-upload', { body: formData, walletAddress, isFormData: true, timeout });
  },
  matchUploaded(fileHash, filenameHash, layerId, walletAddress) {
    return post('/images/match', { body: { file_hash: fileHash, filename_hash: filenameHash, layer_id: layerId }, walletAddress });
  },
  restoreArchived(imageId, originalUrl, thumbnailUrl, previewUrl, walletAddress) {
    return post(`/images/${imageId}/restore`, { body: { original_url: originalUrl, thumbnail_url: thumbnailUrl, preview_url: previewUrl }, walletAddress });
  },
  getColorData(imageIds, walletAddress) {
    return post('/images/color-data', { body: { image_ids: imageIds }, walletAddress });
  },
  getByLayers(layerIds, walletAddress) {
    return post('/images/by-layers', { body: { layer_ids: layerIds }, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Storage (generic proxy)
// ---------------------------------------------------------------------------
export const storage = {
  upload(formData, walletAddress, timeout = 60000) {
    return post('/storage/upload', { body: formData, walletAddress, isFormData: true, timeout });
  },
  deleteFiles(paths, walletAddress) {
    return del('/storage/files', { body: { paths }, walletAddress });
  },
  getPublicUrl(storagePath, walletAddress) {
    return get(`/storage/public-url?path=${encodeURIComponent(storagePath)}`, { walletAddress });
  },
  list(storagePath, walletAddress) {
    return get(`/storage/list?path=${encodeURIComponent(storagePath)}`, { walletAddress });
  },
  validate(originalFile, storagePath, walletAddress) {
    return post('/storage/validate', { body: { storage_path: storagePath, expected_size: originalFile.size }, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Layer Groups
// ---------------------------------------------------------------------------
export const layerGroups = {
  list(projectId, walletAddress) {
    return get(`/layer-groups/${projectId}`, { walletAddress });
  },
  upsert(projectId, groupData, walletAddress) {
    return post(`/layer-groups/${projectId}`, { body: groupData, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------
export const paths = {
  list(projectId, walletAddress, { limit = 200, offset = 0 } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit, offset });
    return get(`/paths?${params}`, { walletAddress });
  },
  create(pathData, walletAddress) {
    return post('/paths', { body: pathData, walletAddress });
  },
  update(pathId, updates, walletAddress) {
    return patch(`/paths/${pathId}`, { body: updates, walletAddress });
  },
  delete(pathId, walletAddress) {
    return del(`/paths/${pathId}`, { walletAddress });
  },
  applyTemplate(projectId, templateId, layers, walletAddress) {
    return post('/paths/apply-template', { body: { project_id: projectId, template_id: templateId, layers }, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------
export const rules = {
  list(projectId, walletAddress, { limit = 200, offset = 0 } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit, offset });
    return get(`/rules?${params}`, { walletAddress });
  },
  create(ruleData, walletAddress) {
    return post('/rules', { body: ruleData, walletAddress });
  },
  update(ruleId, updates, walletAddress) {
    return patch(`/rules/${ruleId}`, { body: updates, walletAddress });
  },
  delete(ruleId, walletAddress) {
    return del(`/rules/${ruleId}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Generations
// ---------------------------------------------------------------------------
export const generations = {
  list(projectId, walletAddress) {
    return get(`/generations?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  create(generationData, walletAddress) {
    return post('/generations', { body: generationData, walletAddress });
  },
  update(generationId, updates, walletAddress) {
    return patch(`/generations/${generationId}`, { body: updates, walletAddress });
  },
  delete(generationId, walletAddress) {
    return del(`/generations/${generationId}`, { walletAddress });
  },
  toggleFavorite(generationId, projectId, walletAddress) {
    return post(`/generations/${generationId}/toggle-favorite`, { body: { project_id: projectId }, walletAddress });
  },
  getFavorites(projectId, walletAddress) {
    return get(`/generations/favorites?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  getExpired(walletAddress) {
    return get('/generations/expired', { walletAddress });
  },
  getFavoritesCount(projectId, walletAddress) {
    return get(`/generations/favorites/count?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  checkValidity(generationId, walletAddress) {
    return get(`/generations/${generationId}/check-validity`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Delegations / Team
// ---------------------------------------------------------------------------
export const delegations = {
  list(projectId, walletAddress) {
    return get(`/delegations?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  create(projectId, ownerWallet, delegateWallet, role, notes, walletAddress) {
    return post('/delegations', {
      body: { project_id: projectId, owner_wallet: ownerWallet, delegate_wallet: delegateWallet, role, notes },
      walletAddress
    });
  },
  createCustom(projectId, ownerWallet, delegateWallet, permissions, notes, walletAddress) {
    return post('/delegations/custom', {
      body: { project_id: projectId, owner_wallet: ownerWallet, delegate_wallet: delegateWallet, permissions, notes },
      walletAddress
    });
  },
  update(delegationId, updates, walletAddress) {
    return patch(`/delegations/${delegationId}`, { body: updates, walletAddress });
  },
  revoke(delegationId, walletAddress) {
    return del(`/delegations/${delegationId}`, { walletAddress });
  },
  getMy(walletAddress) {
    return get(`/delegations/my?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  hasPermission(projectId, walletAddress, permission) {
    return post('/delegations/check-permission', {
      body: { project_id: projectId, wallet_address: walletAddress, permission },
      walletAddress
    });
  },
  // Legacy delegates
  inviteDelegate(projectId, delegateData, walletAddress) {
    return post('/delegations/invite', { body: { project_id: projectId, ...delegateData }, walletAddress });
  },
  getProjectDelegates(projectId, walletAddress) {
    return get(`/delegations/delegates?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  updateDelegatePermissions(delegateId, permissions, walletAddress) {
    return patch(`/delegations/delegates/${delegateId}`, { body: permissions, walletAddress });
  },
  respondToInvitation(delegateId, status, walletAddress) {
    return post(`/delegations/delegates/${delegateId}/respond`, { body: { status }, walletAddress });
  },
  revokeDelegate(delegateId, walletAddress) {
    return del(`/delegations/delegates/${delegateId}`, { walletAddress });
  },
  getMyDelegations(walletAddress) {
    return get(`/delegations/my-delegations?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  getWithProjects(walletAddress) {
    return get(`/delegations/with-projects?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  setLayerPermissions(layerId, delegateId, permissions, walletAddress) {
    return put(`/delegations/layer-permissions/${layerId}/${delegateId}`, { body: permissions, walletAddress });
  },
  getLayerPermissions(layerId, delegateId, walletAddress) {
    return get(`/delegations/layer-permissions/${layerId}/${delegateId}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// User Profiles
// ---------------------------------------------------------------------------
export const userProfiles = {
  get(walletAddress) {
    return get(`/user-profiles/${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  upsert(walletAddress, profile) {
    return put(`/user-profiles/${encodeURIComponent(walletAddress)}`, { body: profile, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Audit / Activity
// ---------------------------------------------------------------------------
export const audit = {
  log(entry, walletAddress) {
    return post('/audit/log', { body: entry, walletAddress });
  },
  getProjectActivity(projectId, limit = 50, offset = 0, walletAddress) {
    return get(`/audit/project/${projectId}?limit=${limit}&offset=${offset}`, { walletAddress });
  },
  getUserActivity(userWallet, limit = 50) {
    return get(`/audit/user/${encodeURIComponent(userWallet)}?limit=${limit}`, { walletAddress: userWallet });
  },
  // Simple activity log (forge_activity_log)
  logActivity({ projectId, activityType, description, metadata, walletAddress }) {
    return post('/audit/activity', {
      body: { project_id: projectId, activity_type: activityType, description, metadata, wallet_address: walletAddress },
      walletAddress
    });
  },
  getActivityLog(projectId, limit = 50, walletAddress) {
    return get(`/audit/activity/${projectId}?limit=${limit}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Project Notes
// ---------------------------------------------------------------------------
export const notes = {
  list(projectId, walletAddress) {
    return get(`/notes?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  create(projectId, note, walletAddress) {
    return post('/notes', { body: { project_id: projectId, ...note }, walletAddress });
  },
  update(noteId, updates, walletAddress) {
    return patch(`/notes/${noteId}`, { body: updates, walletAddress });
  },
  delete(noteId, walletAddress) {
    return del(`/notes/${noteId}`, { walletAddress });
  },
  createReply(noteId, content, walletAddress) {
    return post(`/notes/${noteId}/replies`, { body: { content }, walletAddress });
  },
  getReplies(noteId, walletAddress) {
    return get(`/notes/${noteId}/replies`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Rarity / Color Analysis
// ---------------------------------------------------------------------------
export const rarity = {
  saveSimulation(projectId, results, iterations, walletAddress) {
    return post('/rarity/simulations', { body: { project_id: projectId, results, iterations }, walletAddress });
  },
  loadSimulation(projectId, walletAddress) {
    return get(`/rarity/simulations?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  cleanupExpired(walletAddress) {
    return post('/rarity/cleanup', { walletAddress });
  },
  getColorStats(projectId, walletAddress) {
    return get(`/rarity/color-stats?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  // ML Models
  saveMLModel(projectId, exportedModel, jobId, walletAddress) {
    return post('/rarity/ml-models', { body: { project_id: projectId, model: exportedModel, job_id: jobId }, walletAddress });
  },
  loadMLModel(projectId, walletAddress) {
    return get(`/rarity/ml-models?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  checkMLModel(projectId, walletAddress) {
    return get(`/rarity/ml-model/check/${encodeURIComponent(projectId)}`, { walletAddress });
  },
  upsertMLModel(modelData, walletAddress) {
    return post('/rarity/ml-model/upsert', { body: modelData, walletAddress });
  },
  // Global ML Model
  loadGlobalModel(walletAddress) {
    return get('/rarity/global-model', { walletAddress });
  },
  getGlobalModelVersion(walletAddress) {
    return get('/rarity/global-model/version', { walletAddress });
  },
  contributeToGlobalModel(contributions, walletAddress) {
    return post('/rarity/global-model/contribute', { body: { contributions }, walletAddress });
  },
  checkContribution(hash, walletAddress) {
    return get(`/rarity/global-model/check-contribution?hash=${encodeURIComponent(hash)}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------
export const payments = {
  create(paymentData, walletAddress) {
    return post('/payments', { body: paymentData, walletAddress });
  },
  update(paymentId, updates, walletAddress) {
    return patch(`/payments/${paymentId}`, { body: updates, walletAddress });
  },
  getById(paymentId, walletAddress) {
    return get(`/payments/${paymentId}`, { walletAddress });
  },
  getHistory(projectId, walletAddress) {
    return get(`/payments?project_id=${encodeURIComponent(projectId)}`, { walletAddress });
  },
  getLicense(projectId, walletAddress) {
    return get(`/payments/license/${projectId}`, { walletAddress });
  },
  validateTxHash(transactionHash, excludePaymentId, walletAddress) {
    return post('/payments/validate-tx-hash', {
      body: { transaction_hash: transactionHash, exclude_payment_id: excludePaymentId },
      walletAddress
    });
  },
  revalidate(paymentId, walletAddress) {
    return post(`/payments/${paymentId}/revalidate`, { walletAddress });
  },
  prepare(paymentData, walletAddress) {
    return post('/payments/prepare', { body: paymentData, walletAddress });
  },
  buildTx(paymentId, fromAddress, walletAddress) {
    return post(`/payments/${paymentId}/build-tx`, {
      body: { fromAddress },
      walletAddress: walletAddress || fromAddress,
    });
  },
  submit(paymentId, payload, walletAddress) {
    // Backwards-compat: accept a string signature OR an object with either
    // { signature } or { signedTransaction }.
    const body = typeof payload === 'string' ? { signature: payload } : (payload || {});
    return post(`/payments/${paymentId}/submit`, { body, walletAddress });
  },
  getStatus(paymentId, walletAddress) {
    return get(`/payments/${paymentId}/status`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Project Operations (clone, import)
// ---------------------------------------------------------------------------
export const projectOperations = {
  clone(sourceProjectId, walletAddress) {
    return post('/project-operations/clone', { body: { source_project_id: sourceProjectId }, walletAddress, timeout: 120000 });
  },
  import(formData, walletAddress) {
    return post('/project-operations/import', { body: formData, walletAddress, isFormData: true, timeout: 120000 });
  },
  bulkInsert(table, records, walletAddress, upsertConflict = null) {
    const body = { table, records };
    if (upsertConflict) body.upsert_conflict = upsertConflict;
    return post('/project-operations/bulk-insert', { body, walletAddress, timeout: 60000 });
  },
};

// ---------------------------------------------------------------------------
// Platform / Misc (public endpoints)
// ---------------------------------------------------------------------------
export const platform = {
  getStats() {
    return get('/platform-stats');
  },
  getSocialLinks() {
    return get('/social-links');
  },
  getFeaturedCollections(limit = 12) {
    return get(`/featured-collections?limit=${limit}`);
  },
};

// ---------------------------------------------------------------------------
// Profile (forge_profile — username, email, notification prefs)
// ---------------------------------------------------------------------------
export const profile = {
  get(walletAddress) {
    return get(`/profile/${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
  upsert(walletAddress, profileData) {
    return put(`/profile/${encodeURIComponent(walletAddress)}`, { body: profileData, walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Path Groups (forge_path_groups)
// ---------------------------------------------------------------------------
export const pathGroups = {
  list(projectId, walletAddress, { limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit, offset });
    return get(`/path-groups?${params}`, { walletAddress });
  },
  create(data, walletAddress) {
    return post('/path-groups', { body: data, walletAddress });
  },
  update(id, updates, walletAddress) {
    return patch(`/path-groups/${id}`, { body: updates, walletAddress });
  },
  delete(id, walletAddress) {
    return del(`/path-groups/${id}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Generation Jobs (forge_generation_jobs)
// ---------------------------------------------------------------------------
export const generationJobs = {
  list(projectId, walletAddress, { limit = 20, offset = 0, renderingMode, includeHidden } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit, offset });
    if (renderingMode) params.set('rendering_mode', renderingMode);
    if (includeHidden) params.set('include_hidden', 'true');
    return get(`/generation-jobs?${params}`, { walletAddress });
  },
  update(id, updates, walletAddress) {
    return patch(`/generation-jobs/${id}`, { body: updates, walletAddress });
  },
  getStatus(id, walletAddress) {
    return get(`/generation-jobs/${id}/status`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Curated Jobs (status polling only)
// ---------------------------------------------------------------------------
export const curatedJobs = {
  getStatus(id, walletAddress) {
    return get(`/generation-jobs/curated/${id}/status`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Dashboard Stats (aggregated)
// ---------------------------------------------------------------------------
export const dashboardStats = {
  get(walletAddress) {
    return get(`/dashboard-stats?wallet=${encodeURIComponent(walletAddress)}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Generation Analytics
// ---------------------------------------------------------------------------
export const generationAnalytics = {
  list(projectId, walletAddress, { limit = 100, offset = 0 } = {}) {
    const params = new URLSearchParams({ project_id: projectId, limit, offset });
    return get(`/generation-analytics?${params}`, { walletAddress });
  },
};

// ---------------------------------------------------------------------------
// Default export — all namespaces
// ---------------------------------------------------------------------------
const api = {
  projects,
  layers,
  layerGroups,
  images,
  storage,
  paths,
  rules,
  generations,
  delegations,
  userProfiles,
  audit,
  notes,
  rarity,
  payments,
  projectOperations,
  platform,
  profile,
  pathGroups,
  generationJobs,
  curatedJobs,
  dashboardStats,
  generationAnalytics,
};

export default api;
